import { Component, ElementRef, HostListener, ViewChild, inject } from '@angular/core';
import { FieldType, FieldTypeConfig, FormlyFieldProps } from '@ngx-formly/core';
import { FormlyFieldSelectProps } from '@ngx-formly/core/select';
import { Select } from 'primeng/select';
import { Button } from 'primeng/button';
import { FORM_IMPORTS } from '../../_shared/_imports/form.import';
import { TranslatePipe } from '@ngx-translate/core';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { DrawerModule } from 'primeng/drawer';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { AutoFocusModule } from 'primeng/autofocus';
import { Tag } from 'primeng/tag';
import { AddressSearchPanelComponent } from '../../_shared/_components/address-search-panel/address-search-panel.component';
import { AddressSearchController, AddressSearchMode } from './address-search.controller';
import { AddressSearchProps } from '../../_services/address-cabina-search.service';

export interface SelectFieldProps
  extends FormlyFieldProps, FormlyFieldSelectProps, AddressSearchProps {
  appendTo?: any;
  filter?: boolean;
  optionLabel?: string;
  optionValue?: string;
  dataKey?: string;
  tableOptions?: any[];
  group?: boolean;
  optionGroupLabel?: string;
  optionGroupChildren?: string;
  showClear?: boolean;
  nestedValue?: boolean;
  filterMatchMode?: typeof Select.prototype.filterMatchMode;
  translateLabel?: string;
  optionDescription?: string;
  optionEdit?: (option: any) => void;
  optionDelete?: (option: any) => void;
  /**
   * Per-row gate on top of optionEdit/optionDelete: when omitted every row
   * shows the icon (unchanged default), when provided only rows it returns
   * true for do.
   */
  optionEditVisible?: (option: any) => boolean;
  optionDeleteVisible?: (option: any) => boolean;
  dropdownIcon?: string;
  styleClass?: string;
  filterBy?: string;
  editable?: boolean;
  /**
   * Renders the desktop overlay's option list as a real p-table (columns:
   * label, optionCreatedAt, edit/delete) instead of the default flex-row
   * list. Opt-in only — every other consumer of this type is unaffected.
   */
  tableView?: boolean;
  optionCreatedAt?: string;
  tableNameHeader?: string;
  tableCreatedAtHeader?: string;
  /** Per-row gray badge in the tableView actions column (e.g. "Predefinito"); return null/undefined for no badge. */
  optionBadge?: (option: any) => string | null | undefined;
  /**
   * Opt-in: fa scattare anche `props.change` quando l'utente sceglie dal drawer mobile,
   * dove il valore viene scritto direttamente sul formControl e `(onChange)` di p-select non parte.
   */
  emitChangeOnDrawer?: boolean;
}

@Component({
  selector: 'select-field',
  standalone: true,
  imports: [
    CommonModule,
    ...FORM_IMPORTS,
    Select,
    Button,
    TranslatePipe,
    IconFieldModule,
    InputIconModule,
    InputText,
    DrawerModule,
    TableModule,
    AutoFocusModule,
    Tag,
    AddressSearchPanelComponent,
  ],
  providers: [AddressSearchController],
  template: `
    <p-select
      #dropdown
      [ngClass]="{ 'p-disabled': props.readonly }"
      class="w-full {{ props.styleClass ?? '' }}"
      (onClick)="handleDropdownClick($event, dropdown)"
      [panelStyleClass]="
        isMobile
          ? 'hide-dropdown-panel'
          : props.tableView
            ? 'select-table-view'
            : ''
      "
      [dropdownIcon]="props.dropdownIcon ?? 'fa-solid fa-caret-down'"
      [placeholder]="props.placeholder"
      [formControl]="formControl"
      [formlyAttributes]="field"
      [options]="$any(visibleOptions)"
      autocomplete="false"
      [showClear]="props.showClear ?? false"
      [required]="props.required ?? false"
      [filter]="props.filter && !isAddressMode"
      (onChange)="props.change && props.change(field, $event)"
      (onHide)="onPanelHidden()"
      [virtualScroll]="true"
      [virtualScrollItemSize]="43"
      fluid="true"
      [filterMatchMode]="props.filterMatchMode ?? 'contains'"
      [optionLabel]="props.optionLabel ?? 'label'"
      [editable]="props.editable ?? false"
      [optionValue]="
        props.nestedValue
          ? undefined
          : props.optionValue
            ? props.optionValue
            : undefined
      "
      [dataKey]="props.dataKey ?? undefined"
      optionDisabled="disabled"
      [group]="props.group"
      [optionGroupLabel]="
        props.optionGroupLabel ? props.optionGroupLabel : 'label'
      "
      [optionGroupChildren]="
        props.optionGroupChildren ? props.optionGroupChildren : 'items'
      "
      [appendTo]="props.appendTo ?? 'body'"
      [filterBy]="props.filterBy ?? undefined"
    >
      <ng-template #selectedItem let-selected>
        @if (props.translateLabel) {
          {{
            props.translateLabel +
              '.' +
              (props.optionValue ? selected[props.optionValue] : selected)
              | translate
          }}
        } @else {
          {{ selected[props.optionLabel ?? 'label'] }}
        }
      </ng-template>
      <ng-template #optionLabel let-el>
        @if (props.translateLabel) {
          {{
            props.translateLabel +
              '.' +
              (props.optionValue ? el[props.optionValue] : el) | translate
          }}
        } @else {
          {{ el[props.optionLabel ?? 'label'] }}
        }

        @if (props.optionDescription) {
          <div class="text-xs italic">
            {{ el[props.optionDescription] }}
          </div>
        }
      </ng-template>
      <ng-template let-el #item>
        @if (props.optionEdit || props.optionDelete) {
          <div class="flex items-center justify-between gap-2 w-full">
            <div class="{{ props.group ? 'pl-4' : '' }}">
              <ng-container
                *ngTemplateOutlet="optionLabel; context: { $implicit: el }"
              />
            </div>
            <div class="flex items-center gap-1">
              @if (showEditIcon(el)) {
                <p-button
                  icon="fas fa-edit"
                  variant="text"
                  size="small"
                  (onClick)="onOptionEdit(el, $event)"
                />
              }
              @if (showDeleteIcon(el)) {
                <p-button
                  icon="fas fa-trash"
                  severity="danger"
                  variant="text"
                  size="small"
                  (onClick)="onOptionDelete(el, $event)"
                />
              }
            </div>
          </div>
        } @else {
          <div class="{{ props.group ? 'pl-4' : '' }}">
            <ng-container
              *ngTemplateOutlet="optionLabel; context: { $implicit: el }"
            />
          </div>
        }
      </ng-template>
      <ng-template #header>
        @if (props.addressSearch && !props.group) {
          <app-address-search-panel
            [mode]="addressSearch.mode()"
            [query]="addressSearch.query()"
            [suggestions]="addressSearch.suggestions()"
            [loading]="addressSearch.loading()"
            [placesUnavailable]="addressSearch.placesUnavailable()"
            [resolved]="addressSearch.resolved()"
            (modeChange)="onAddressModeChange($event)"
            (queryChange)="addressSearch.onQueryChange($event)"
            (suggestionSelected)="addressSearch.selectSuggestion($event)"
          />
        }
        @if (props.tableView) {
          <p-table
            [value]="$any(props.tableOptions ?? props.options)"
            [dataKey]="props.dataKey ?? 'id'"
            selectionMode="single"
            [scrollable]="true"
            scrollHeight="22rem"
            styleClass="w-full p-2"
            (onRowSelect)="onTableRowSelect($event.data, dropdown)"
          >
            <ng-template #header>
              <tr>
                <th>{{ props.tableNameHeader }}</th>
                <th>{{ props.tableCreatedAtHeader }}</th>
                @if (props.optionEdit || props.optionDelete || props.optionBadge) {
                  <th class="w-0"></th>
                }
              </tr>
            </ng-template>
            <ng-template #body let-el>
              <tr [pSelectableRow]="el">
                <td>{{ el[props.optionLabel ?? 'label'] }}</td>
                <td>{{ props.optionCreatedAt ? el[props.optionCreatedAt] : '' }}</td>
                @if (props.optionEdit || props.optionDelete || props.optionBadge) {
                  <td class="w-0">
                    <div class="flex items-center justify-end gap-1">
                      @if (props.optionBadge && props.optionBadge(el); as badgeText) {
                        <p-tag [value]="badgeText" severity="secondary" rounded />
                      }
                      @if (showEditIcon(el)) {
                        <p-button
                          icon="fas fa-edit"
                          variant="text"
                          size="small"
                          (onClick)="onOptionEdit(el, $event)"
                        />
                      }
                      @if (showDeleteIcon(el)) {
                        <p-button
                          icon="fas fa-trash"
                          severity="danger"
                          variant="text"
                          size="small"
                          (onClick)="onOptionDelete(el, $event)"
                        />
                      }
                    </div>
                  </td>
                }
              </tr>
            </ng-template>
            <ng-template #emptyMessage>
              <tr>
                <td
                  [attr.colspan]="
                    props.optionEdit || props.optionDelete || props.optionBadge
                      ? 3
                      : 2
                  "
                >
                  {{ 'primeng.emptyMessage' | translate }}
                </td>
              </tr>
            </ng-template>
          </p-table>
        }
      </ng-template>
    </p-select>

    <p-drawer
      [(visible)]="drawer"
      [position]="'bottom'"
      styleClass="h-auto min-h-[655px] pt-22"
      baseZIndex="1104"
      [maskStyle]="$any('z-index:1103')"
      (onShow)="onShow($event)"
    >
      <ng-template #closeicon>
        <i class="fa-solid fa-caret-up text-xl"></i>
      </ng-template>
      <ng-template #header>
        <div class="flex items-center gap-2">
          <span class="font-bold">{{
            props.label != '' ? props.label : props.placeholder
          }}</span>
        </div>
      </ng-template>
      <p-table
        #dt2
        scrollHeight="500px"
        [virtualScroll]="props.group ? false : true"
        [virtualScrollItemSize]="46"
        [value]="$any(props.tableOptions ?? visibleOptions)"
        selectionMode="single"
        [(selection)]="selected"
        [globalFilterFields]="
          $any(props.filterBy ?? props.optionLabel ?? 'label').split(',')
        "
        (selectionChange)="selectionChange($event)"
        [rowGroupMode]="props.group ? 'subheader' : undefined"
        [groupRowsBy]="props.group ? 'optionGroupLabel' : undefined"
        [sortField]="props.group ? 'optionGroupLabel' : undefined"
        sortMode="single"
        [dataKey]="props.group ? 'optionGroupLabel' : undefined"
      >
        @if (props.filter || props.addressSearch) {
          <ng-template #caption>
            @if (props.addressSearch && !props.group) {
              <app-address-search-panel
                [mode]="addressSearch.mode()"
                [query]="addressSearch.query()"
                [suggestions]="addressSearch.suggestions()"
                [loading]="addressSearch.loading()"
                [placesUnavailable]="addressSearch.placesUnavailable()"
                [resolved]="addressSearch.resolved()"
                (modeChange)="onDrawerAddressModeChange($event)"
                (queryChange)="addressSearch.onQueryChange($event)"
                (suggestionSelected)="addressSearch.selectSuggestion($event)"
              />
            }
            @if (props.filter && !isAddressMode) {
              <div class="flex">
                <p-iconfield iconPosition="left" class="w-full">
                  <input
                    #search
                    pInputText
                    [pAutoFocus]="true"
                    type="text"
                    (input)="
                      dt2.filterGlobal($any($event.target).value, 'contains')
                    "
                  /><p-inputicon>
                    <i class="fas fa-search"></i>
                  </p-inputicon>
                </p-iconfield>
              </div>
            }
          </ng-template>
        }

        @if (props.group) {
          <ng-template #groupheader let-row>
            <tr>
              <td colspan="2">
                <span class="font-bold">{{ row['optionGroupLabel'] }}</span>
              </td>
            </tr>
          </ng-template>
        }

        <ng-template #body let-el>
          <tr [pSelectableRow]="el">
            <td>
              @if (props.translateLabel) {
                {{
                  props.translateLabel +
                    '.' +
                    (props.optionValue ? el[props.optionValue] : el) | translate
                }}
              } @else {
                {{ el[props.optionLabel ? props.optionLabel : 'label'] }}
              }
              @if (props.optionDescription) {
                <div class="text-xs italic">
                  {{ el[props.optionDescription] }}
                </div>
              }
            </td>
            @if (showEditIcon(el)) {
              <td class="w-0">
                <p-button
                  icon="fas fa-edit"
                  variant="text"
                  size="small"
                  (onClick)="onOptionEdit(el, $event)"
                />
              </td>
            }
            @if (showDeleteIcon(el)) {
              <td class="w-0">
                <p-button
                  icon="fas fa-trash"
                  severity="danger"
                  variant="text"
                  size="small"
                  (onClick)="onOptionDelete(el, $event)"
                />
              </td>
            }
          </tr>
        </ng-template>
        <ng-template #emptyMessage>
          {{ 'primeng.emptyMessage' | translate }}
        </ng-template>
      </p-table>
    </p-drawer>
  `,
  styles: `
    :host ::ng-deep {
      p-select {
        @apply w-full;
        display: flex !important;
        align-items: center !important;
        flex-wrap: nowrap !important;
        width: 100% !important;
      }
      p-select > .p-select-label {
        flex-grow: 1 !important;
        min-width: 0 !important;
        width: auto !important;
      }
      p-select > .p-select-clear-icon,
      p-select > .p-select-dropdown {
        flex: 0 0 auto !important;
      }
      p-select > p-overlay {
        position: absolute !important;
      }
      p-overlay {
        @apply w-full;
      }
    }
  `,
})
export class SelectFieldType extends FieldType<
  FieldTypeConfig<SelectFieldProps>
> {
  protected readonly addressSearch = inject(AddressSearchController);
  private readonly _elementRef = inject(ElementRef<HTMLElement>);

  isMobile: boolean = false;
  @ViewChild('dt2') dt2!: any;
  @ViewChild('search', { static: false }) search?: ElementRef<HTMLInputElement>;
  @ViewChild('dropdown') private _dropdownRef?: Select;

  /** Options bindate al dropdown: la lista completa, o solo la cabina risolta dall'indirizzo. */
  private _visibleSource: unknown[] = [];
  private _visibleCacheKey: unknown = null;
  private _visibleCache: unknown[] = [];

  get visibleOptions(): unknown[] {
    const source = (this.props.options ?? []) as Record<string, unknown>[];
    const matched = this.addressSearch.active() ? this.addressSearch.matched() : null;
    const key = `${this.addressSearch.active()}|${matched?.id ?? ''}|${source.length}|${this.formControl.value}`;
    if (this._visibleCacheKey !== key || this._visibleSource !== source) {
      this._visibleSource = source;
      this._visibleCacheKey = key;
      this._visibleCache = this.addressSearch.active()
        ? this._withSelectedPreserved(
            this.addressSearch.filterOptions(source, this.props.addressSearchMatchKeys ?? ['id', 'cod_ac']),
            source,
          )
        : source;
    }
    return this._visibleCache;
  }

  /**
   * 🔥 [options] è bindato anche per risolvere l'etichetta del valore già
   * selezionato (#selectedItem): se la ricerca per indirizzo filtra via
   * l'opzione oggi selezionata, PrimeNG non trova più l'oggetto e il trigger
   * mostra il campo vuoto pur avendo ancora un valore nel form. La si
   * ripristina nell'elenco anche quando non risponde all'indirizzo cercato.
   */
  private _withSelectedPreserved<T extends Record<string, unknown>>(filtered: T[], source: T[]): T[] {
    const raw = this.formControl.value;
    if (raw == null) return filtered;
    const optionValueKey = this.props.nestedValue ? undefined : this.props.optionValue;
    const already = new Set(filtered);
    const preserved = source.filter((option) => {
      if (already.has(option)) return false;
      const optionKey = optionValueKey ? option[optionValueKey] : option;
      return optionKey === raw;
    });
    return preserved.length ? [...filtered, ...preserved] : filtered;
  }

  get isAddressMode(): boolean {
    return !!this.props.addressSearch && this.addressSearch.mode() === 'address';
  }

  ngOnInit() {
    this.checkIfMobile();

    this.formControl.valueChanges.subscribe((value) => {
      if (value == null) {
        this.selected = null;
        this.dt2?.clear();
      }
    });
  }

  @HostListener('window:resize', [])
  onResize() {
    this.checkIfMobile();
  }

  /**
   * The tableView overlay is appended to `body` and positioned once at open
   * time; PrimeNG only auto-closes it on scroll of a CSS overflow:auto/scroll
   * ancestor of the trigger (ConnectedOverlayScrollHandler), which misses a
   * plain window/page scroll — leaving an absolutely-positioned panel that
   * visually scrolls away with the page instead of closing. Force the close
   * ourselves for this case; other `select` consumers are unaffected.
   */
  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (this.props.tableView) {
      this._dropdownRef?.hide();
    }
  }

  private checkIfMobile() {
    this.isMobile = window.innerWidth <= 768;
  }
  drawer: boolean = false;

  tableOptions: any = [];

  selected: any;

  handleDropdownClick(event: MouseEvent, dropdown: any) {
    if (this.props.readonly) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (this.isMobile) {
      event.preventDefault();
      event.stopPropagation();
      this.drawer = !this.drawer;
      return;
    }

    this.ensureSpaceBelow();
  }

  /**
   * 🔥 PrimeNG apre il pannello sopra il trigger quando non c'è spazio sotto
   * nel viewport (comportamento interno, non configurabile): in dialog corti
   * questo lo fa sembrare staccato dal campo. Scrollando il trigger prima
   * dell'apertura si garantisce spazio sufficiente sotto, così il pannello si
   * apre sempre ancorato correttamente. `instant` per evitare che uno scroll
   * animato (es. `scroll-behavior: smooth` sull'ancestor) sia ancora in corso
   * quando PrimeNG calcola la posizione.
   */
  private ensureSpaceBelow(): void {
    const rect = this._elementRef.nativeElement.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    if (spaceBelow < 320) {
      this._elementRef.nativeElement.scrollIntoView({ block: 'center', behavior: 'instant' });
    }
  }

  onAddressModeChange(mode: AddressSearchMode): void {
    // ⚠️ il filtro testuale nativo resta valorizzato anche quando l'input è
    // nascosto: va azzerato o la lista resterebbe filtrata sul testo precedente.
    this._dropdownRef?.resetFilter();
    this.addressSearch.setMode(mode);
    setTimeout(() => this._dropdownRef?.scroller?.scrollToIndex(0));
  }

  onDrawerAddressModeChange(mode: AddressSearchMode): void {
    this.dt2?.clear();
    this.addressSearch.setMode(mode);
  }

  onPanelHidden(): void {
    this.addressSearch.reset();
  }

  selectionChange(event: any) {
    if (this.props.readonly) {
      return;
    }

    if (!event) {
      this.clearSelection();
      return;
    }

    let data = event;

    if (this.props.group) {
      data = ((this.props.options ?? []) as any[])
        .flatMap((g) => g[this.props.optionGroupChildren!])
        .find((c) => c.id === event.id);
    }

    if (this.props.optionValue && !this.props.nestedValue) {
      this.formControl.setValue(event[this.props.optionValue]);
    } else {
      this.formControl.setValue(data);
    }

    this.selected = data;
    this.drawer = false;

    if (this.props.emitChangeOnDrawer) {
      this.props.change?.(this.field, { value: this.formControl.value });
    }
  }

  /** Row click handler for the tableView p-table (desktop overlay only). */
  onTableRowSelect(option: any, dropdown: any): void {
    if (this.props.optionValue && !this.props.nestedValue) {
      this.formControl.setValue(option[this.props.optionValue]);
    } else {
      this.formControl.setValue(option);
    }
    dropdown.hide();
  }

  clearSelection() {
    this.selected = null;
    this.formControl.setValue(undefined);
    this.dt2?.clear();
    this.drawer = false;
  }

  onShow(event: any) {
    if (this.props.readonly) {
      this.drawer = false;
      return;
    }

    const options = this.props.options as any[];

    if (this.formControl.value) {
      const selectedIndex = options.findIndex((el) =>
        this.props.optionValue
          ? el[this.props.optionValue] == this.formControl.value
          : el == this.formControl.value,
      );
      this.selected = options[selectedIndex];
      setTimeout(() => {
        this.search?.nativeElement.focus();

        if (
          this.dt2?.scroller &&
          typeof this.dt2.scroller.scrollToIndex === 'function'
        ) {
          this.dt2.scroller.scrollToIndex(selectedIndex);
        } else {
          console.warn(
            'Scroller non disponibile o metodo scrollToIndex non trovato',
          );
        }
      });
    }
  }

  showEditIcon(option: any): boolean {
    return (
      !!this.props.optionEdit &&
      (!this.props.optionEditVisible || this.props.optionEditVisible(option))
    );
  }

  showDeleteIcon(option: any): boolean {
    return (
      !!this.props.optionDelete &&
      (!this.props.optionDeleteVisible ||
        this.props.optionDeleteVisible(option))
    );
  }

  onOptionEdit(option: any, event: Event): void {
    event.stopPropagation();
    this.props.optionEdit?.(option);
  }

  onOptionDelete(option: any, event: Event): void {
    event.stopPropagation();
    this.props.optionDelete?.(option);
  }
}
